import React from 'react';
import { Theme, styled } from '@mui/material/styles';
import TrimbleMaps, { LngLatBoundsLike } from '@trimblemaps/trimblemaps-js';
import { AllGeoJSON, bbox } from '@turf/turf';

import { Lane } from '../../interfaces/services/lanes';
import { MapArea } from '../../interfaces/services/geography';
import { MapLayers, MapSources } from '../../utils/maps/enums';
import {
    fitMapToBounds,
    handleFillPopupMouseEnter,
    handleLineStringPopupMouseEnter,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    trimbleMapsPopup,
} from '../../utils/maps/mapUtils';
import {
    addLaneDetailsCenterLayer,
    addLaneDetailsLineLayer,
    addLaneDetailsLineHoverLayer,
    addLaneDetailsAreasPolygonFillLayer,
    addLaneDetailsAreasPolygonLineLayer,
} from '../../utils/maps/layers/laneDetails';
import { addLaneDetailsSource, LaneDetailsMapSourceProperties } from '../../utils/maps/sources/laneDetails';
import MapSpinner from '../Loaders/MapSpinner';

import '../../assets/CSS/trimbleMaps.css';

const classesPrefix = 'laneDetailsMap';

const classes = {
    map: `${classesPrefix}-map`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.map}`]: {
            height: '100%',
            minHeight: '300px',
        },
    };
});

interface LaneDetailsMapProps {
    /** Indicator to show loading spinner while data is fetching. */
    isFetchingData: boolean;
    /** MUI theme for styling the map layers */
    theme: Theme;
    /** Lanes to add to the map */
    lanes: Lane[];
    /** Areas to add to the map */
    areas: MapArea[];
    /** Function to handle clicking an area */
    handleAreaClick: (areaId: string) => void;
    /** Indicator to trigger a map resize function. */
    resize?: boolean;
}

interface LaneDetailsMapState {
    mapStyleLoaded: boolean;
}

let map: TrimbleMaps.Map | null = null;
const mapContainer = React.createRef<HTMLDivElement>();

class LaneDetailsMap extends React.Component<LaneDetailsMapProps, LaneDetailsMapState> {

    private hoveredPolygonId: string | number | null | undefined;

    constructor(props: LaneDetailsMapProps) {
        super(props);

        TrimbleMaps.APIKey = '173580F5C6F48F44B5006F78909958C1';

        this.state = {
            mapStyleLoaded: false,
        };

        this.hoveredPolygonId = null;
    }

    componentDidMount(): void {
        const latitude = 39.828347;
        const longitude = -98.579488;

        if (!mapContainer.current) { return; }

        map = new TrimbleMaps.Map({
            container: mapContainer.current, // Ties to an HTML DOM element
            center: [longitude, latitude], // Sets initial center of map
            style: TrimbleMaps.Common.Style.DATADARK, // Sets initial map style
            minZoom: 1.55, // To stop the zoom from showing multiple worlds
            zoom: 3, // Sets initial zoom level
        }).addControl(
            // Adds the zoom in/out and compass buttons
            new TrimbleMaps.NavigationControl()
        ).addControl(
            new TrimbleMaps.FullscreenControl()
        );

        map.setRegion(TrimbleMaps.Common.Region.WW);

        // README: The event `style.load` is no longer a public event of mapbox. We will need to use some combination of using `styledata` and `map.isStyleLoaded()` at some point.
        // However, these do not work the same and there are several open issues with mapbox that have yet to be resolved, such as this one:
        // https://github.com/mapbox/mapbox-gl-js/issues/9779
        // Once a map style is loaded, add any layers that were on back to the map
        map.on('style.load', (): void => {
            // The style.load event can fire after the component has been unmounted.
            if (map === null) {
                return;
            }

            this.removeLanesEventHandlers();
            this.addLaneDetailsToMap();

            // HACK: trigger a resize function to make map take up full width
            map.resize();
            this.setState({ mapStyleLoaded: true });
        });
    }

    componentDidUpdate(prevProps: LaneDetailsMapProps): void {
        // if the map is null, hold any CDU updates
        if (map === null) {
            return;
        }

        if (this.state.mapStyleLoaded === false) {
            return;
        }

        // add lane details to map if props have updated
        if (prevProps.lanes !== this.props.lanes || prevProps.areas !== this.props.areas) {
            this.removeLanesEventHandlers();
            this.addLaneDetailsToMap();
        }

        // if resize prop is passed and is true force map to resize.
        if (this.props.resize === true) {
            map.resize();
        }
    }

    componentWillUnmount(): void {
        this.removeLanesEventHandlers();
    }

    handleLaneCenterLayerClick = (e: any): void => {
        if (map) {
            // Get the layer that was clicked
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the click function
            if (currentFeature.layer.id === MapLayers.laneCenterLayer) {
                // pull custom properties off the geoJson obj to send to parent
                const { properties }: { properties: LaneDetailsMapSourceProperties; } = e.features[0];
                // handle action from parent component
                this.props.handleAreaClick(properties.id);
            }
        }
    };

    handleFillLayerMouseenter = (e: TrimbleMaps.MapMouseEvent & {
        features?: TrimbleMaps.GeoJSONFeature[];
    } & TrimbleMaps.EventData): void => {
        if (map) {
            handleFillPopupMouseEnter(map, e, trimbleMapsPopup);

            // Update the feature state for the feature under the mouse.
            if (e.features && e.features.length > 0) {
                if (this.hoveredPolygonId !== null) {
                    map.setFeatureState(
                        { source: MapSources.laneDetailsSource, id: this.hoveredPolygonId },
                        { hover: false }
                    );
                }
                this.hoveredPolygonId = e.features[0].id;
                map.setFeatureState(
                    { source: MapSources.laneDetailsSource, id: this.hoveredPolygonId },
                    { hover: true }
                );
            }
        }
    };

    handleFillLayerMouseleave = (): void => {
        if (map) {
            handlePopupMouseLeave(map, trimbleMapsPopup);

            // Update the feature state of the previously hovered feature.
            if (this.hoveredPolygonId !== null) {
                map.setFeatureState(
                    { source: MapSources.laneDetailsSource, id: this.hoveredPolygonId },
                    { hover: false }
                );
            }
            this.hoveredPolygonId = null;
        }
    };

    handleFillLayerClick = (e: any): void => {
        if (map) {
            // Get the layer that was clicked
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the click function
            if (currentFeature.layer.id === MapLayers.areaPolygonFillLayer) {
                // pull custom properties off the geoJson obj to send to parent
                const { properties }: { properties: LaneDetailsMapSourceProperties; } = e.features[0];
                // handle action from parent component
                this.props.handleAreaClick(properties.id);
            }
        }
    };

    addLaneDetailsToMap = (): void => {
        if (map) {
            // add map source
            const laneDetailsSource = addLaneDetailsSource({
                map,
                lanes: this.props.lanes || [],
                areas: this.props.areas || [],
                areaNames: [this.props.lanes[0]?.delivery_service_area_name || '', this.props.lanes[0]?.pickup_service_area_name || ''],
                theme: this.props.theme,
            });

            addLaneDetailsAreasPolygonFillLayer({
                map,
                mapSource: MapSources.laneDetailsSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.areaPolygonFillLayer, this.handleFillLayerMouseenter);
            map.on('mouseleave', MapLayers.areaPolygonFillLayer, this.handleFillLayerMouseleave);
            map.on('click', MapLayers.areaPolygonFillLayer, this.handleFillLayerClick);

            addLaneDetailsAreasPolygonLineLayer({
                map,
                mapSource: MapSources.laneDetailsSource,
                isVisible: true,
            });

            addLaneDetailsLineLayer({
                map,
                mapSource: MapSources.laneDetailsSource,
                isVisible: true,
            });

            addLaneDetailsLineHoverLayer({
                map,
                mapSource: MapSources.laneDetailsSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.laneLineHoverLayer, (e: any): void => {
                handleLineStringPopupMouseEnter(map, MapLayers.laneLineHoverLayer, e, trimbleMapsPopup);
            });
            map.on('mouseleave', MapLayers.laneLineHoverLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            // add lane centers
            addLaneDetailsCenterLayer({
                map,
                mapSource: MapSources.laneDetailsSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.laneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.laneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            map.on('click', MapLayers.laneCenterLayer, this.handleLaneCenterLayerClick);

            const { data } = laneDetailsSource;
            if ((data as GeoJSON.FeatureCollection<GeoJSON.Point | GeoJSON.LineString | GeoJSON.Geometry, LaneDetailsMapSourceProperties>).features.length > 0) {
                const boundingBox = bbox(data as AllGeoJSON);
                fitMapToBounds({
                    map,
                    bounds: boundingBox as LngLatBoundsLike,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    removeLanesEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.areaPolygonFillLayer) !== undefined) {
                map.off('mouseenter', MapLayers.areaPolygonFillLayer, this.handleFillLayerMouseenter);
                map.off('mouseleave', MapLayers.areaPolygonFillLayer, this.handleFillLayerMouseleave);
                map.off('click', MapLayers.areaPolygonFillLayer, this.handleFillLayerClick);
            }

            if (map.getLayer(MapLayers.laneLineHoverLayer) !== undefined) {
                map.off('mouseenter', MapLayers.laneLineHoverLayer, (e): void => {
                    handleLineStringPopupMouseEnter(map, MapLayers.laneLineHoverLayer, e, trimbleMapsPopup);
                });
                map.off('mouseleave', MapLayers.laneLineHoverLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }

            if (map.getLayer(MapLayers.laneCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.laneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.laneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
                map.off('click', MapLayers.laneCenterLayer, this.handleLaneCenterLayerClick);
            }
        }
    };

    render(): React.JSX.Element {
        return (
            <StyledDiv
                id='trimble-map-container'
                ref={mapContainer}
                className={classes.map}
                data-qa='laneDetailsMap-container'
            >
                {
                    this.props.isFetchingData &&
                    <MapSpinner />
                }
            </StyledDiv>
        );
    }

}

export default LaneDetailsMap;
